import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Badge, Select, Table, Modal } from '../components/Common';

/**
 * Governance: whether each application is being tested as often as the client's
 * own policy requires, and what falls due next.
 */

// A duration is entered as a number plus a unit and stored as months, so the
// arithmetic has one unit throughout.
const COUNTS = [0, 1, 2, 3, 4, 6, 9, 12, 18, 24, 36];
const YEAR_OPTS = [0, 1, 2, 3, 4, 5];
const MONTH_OPTS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const toMonths = (count, unit) => (unit === 'years' ? count * 12 : count);
const toMonthsYM = (years, months) => (years * 12) + months;
const fromMonthsYM = (totalMonths) => ({ years: Math.floor(totalMonths / 12), months: totalMonths % 12 });
const fromMonths = (months) =>
  months % 12 === 0 && months >= 12 ? { count: months / 12, unit: 'years' } : { count: months, unit: 'months' };
const describe = (months) => {
  const { count, unit } = fromMonths(months);
  return `${count} ${count === 1 ? unit.replace(/s$/, '') : unit}`;
};

// Severities that carry a remediation deadline. Info is absent deliberately:
// informational findings are observations, not defects.
const SLA_SEVERITIES = ['Critical', 'High', 'Medium', 'Low'];
const SEVERITY_COLOUR = { Critical: '#dc2626', High: '#ea580c', Medium: '#eab308', Low: '#3b82f6' };

const STATUS = {
  never_tested: { label: 'Never tested', colour: '#dc2626' },
  // Tested, but with no end date recorded — compliance you cannot evidence
  // with a date does not count, so it sits with the failures.
  undated: { label: 'Date missing', colour: '#a855f7' },
  overdue: { label: 'Overdue', colour: '#ea580c' },
  due_soon: { label: 'Due soon', colour: '#eab308' },
  compliant: { label: 'Compliant', colour: '#22c55e' },
};

function StatTile({ label, value, colour, hint }) {
  return (
    <Card style={{ flex: '1 1 150px', minWidth: 150 }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: colour || 'var(--text)' }}>{value}</div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{label}</div>
      {hint && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>{hint}</div>}
    </Card>
  );
}

export default function Governance() {
  const { apiFetch, user, hasGroup } = useAuth();
  const navigate = useNavigate();

  const isMspSide = hasGroup('Super Admins') || (!user?.clientId && hasGroup('Engagement Admins'));
  const canEditPolicy = hasGroup('Client Admins') || hasGroup('Super Admins');
  // The same groups the engagement route accepts. Offering the button to
  // anyone else would produce a row of 403s.
  const canCreateEngagements = hasGroup('Super Admins') || hasGroup('Engagement Admins') ||
    hasGroup('Client Admins');

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(user?.clientId || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [includeOutOfScope, setIncludeOutOfScope] = useState(false);
  const [pciOnly, setPciOnly] = useState(false);
  const [internetFacingOnly, setInternetFacingOnly] = useState(false);
  const [remediation, setRemediation] = useState(null);
  const [tab, setTab] = useState('cadence');

  // Policy form state, kept separate so it can be edited before saving.
  const [interval, setIntervalState] = useState({ count: 1, unit: 'years' });
  const [intervalYM, setIntervalYM] = useState({ years: 1, months: 0 });
  const [lookahead, setLookahead] = useState({ count: 3, unit: 'months' });
  const [lookaheadYM, setLookaheadYM] = useState({ years: 0, months: 3 });
  const [sla, setSla] = useState({ Critical: 7, High: 30, Medium: 90, Low: 180 });
  const [policyOpen, setPolicyOpen] = useState(false);
  // The second testing policy: PCI segmentation, measured over networks
  // rather than applications.
  const [segData, setSegData] = useState(null);
  const [segIntervalYM, setSegIntervalYM] = useState({ years: 0, months: 6 });
  const [segAllNetworks, setSegAllNetworks] = useState(false);
  const [cloning, setCloning] = useState('');
  // Opening engagements for applications that are overdue. `queue` is what the
  // modal is about to create; empty means the modal is closed.
  const [queue, setQueue] = useState([]);
  const [createType, setCreateType] = useState('Application Penetration Test');
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);

  const [savingPolicy, setSavingPolicy] = useState(false);
  const [policySaved, setPolicySaved] = useState(false);

  useEffect(() => {
    if (!isMspSide) return;
    apiFetch('/clients').then(r => r.json()).then(d => {
      if (Array.isArray(d)) setClients(d.filter(c => c.is_active !== false));
    }).catch(() => {});
  }, [apiFetch, isMspSide]);

  const load = useCallback(async () => {
    if (!clientId) { setData(null); return; }
    setLoading(true);
    setError('');
    try {
      const query = `clientId=${clientId}&pciOnly=${pciOnly}&internetFacingOnly=${internetFacingOnly}`;
      const [res, remRes, segRes] = await Promise.all([
        apiFetch(`/governance/compliance?${query}&includeOutOfScope=${includeOutOfScope}`),
        apiFetch(`/governance/remediation?${query}`),
        apiFetch(`/governance/segmentation-compliance?clientId=${clientId}&allNetworks=${segAllNetworks}`),
      ]);
      const segD = await segRes.json().catch(() => null);
      if (segRes.ok && segD) {
        setSegData(segD);
        setSegIntervalYM(fromMonthsYM(segD.policy.segmentation_interval_months));
      } else {
        setSegData(null);
      }
      const d = await res.json().catch(() => null);
      if (!res.ok || !d) {
        setData(null);
        setError(d?.error || 'Could not load compliance data');
      } else {
        setData(d);
        // Open on arrival only while there is nothing set yet.
        if (!d.policy.configured) setPolicyOpen(true);
        setIntervalState(fromMonths(d.policy.testing_interval_months));
        setIntervalYM(fromMonthsYM(d.policy.testing_interval_months));
        setLookahead(fromMonths(d.policy.lookahead_months));
        setLookaheadYM(fromMonthsYM(d.policy.lookahead_months));
        setSla({
          Critical: d.policy.sla_critical_days, High: d.policy.sla_high_days,
          Medium: d.policy.sla_medium_days, Low: d.policy.sla_low_days,
        });
      }
      const rem = remRes.ok ? await remRes.json().catch(() => null) : null;
      setRemediation(rem);
    } catch {
      setData(null);
      setRemediation(null);
      setError('Could not load compliance data');
    }
    setLoading(false);
  }, [apiFetch, clientId, includeOutOfScope, pciOnly, internetFacingOnly, segAllNetworks]);

  useEffect(() => { load(); }, [load]);

  // Run the same test again, against the same scope. The endpoint copies the
  // type, the compliance flags and every scope item, and deliberately leaves
  // the dates, the tester and the billing fields empty — a clone is a test
  // that has not happened yet, so it must not draw on the statement of work
  // the previous one used.
  const cloneTest = async (row) => {
    if (!row.last_engagement_id) return;
    const booked = row.open_engagements > 0
      ? `

Note: ${row.open_engagements} segmentation test(s) for this network are already open. ` +
        `Cloning now will create another.`
      : '';
    if (!window.confirm(
      `Create a new engagement with the same scope as "${row.last_engagement_name || 'the last test'}"?

` +
      `It starts as New with no dates, tester or statement of work.${booked}`
    )) return;

    setCloning(row.id);
    try {
      const res = await apiFetch(`/engagements/${row.last_engagement_id}/clone`, {
        method: 'POST', body: JSON.stringify({}),
      });
      const d = await res.json().catch(() => null);
      if (!res.ok || !d?.id) {
        window.alert(d?.error || `Could not clone the engagement (HTTP ${res.status})`);
        return;
      }
      // Straight to the new engagement: the next thing anyone does is set its
      // dates and tester, and leaving them on this page to go and find it is
      // the step that gets forgotten.
      navigate(`/engagements/${d.id}`);
    } catch (e) {
      window.alert(String(e.message || e));
    } finally {
      setCloning('');
    }
  };

  const savePolicy = async () => {
    setSavingPolicy(true);
    setPolicySaved(false);
    const res = await apiFetch('/governance/policy', {
      method: 'PUT',
      body: JSON.stringify({
        clientId,
        testingIntervalMonths: toMonthsYM(intervalYM.years, intervalYM.months),
        lookaheadMonths: toMonthsYM(lookaheadYM.years, lookaheadYM.months),
        segmentationIntervalMonths: toMonthsYM(segIntervalYM.years, segIntervalYM.months),
        slaCriticalDays: sla.Critical, slaHighDays: sla.High,
        slaMediumDays: sla.Medium, slaLowDays: sla.Low,
      }),
    });
    setSavingPolicy(false);
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setError(d.error || 'Could not save the policy');
      return;
    }
    setPolicySaved(true);
    setTimeout(() => setPolicySaved(false), 2500);
    load();
  };

  const ENGAGEMENT_TYPES = ['Application Penetration Test', 'API Penetration Test',
                           'Mobile Application Test', 'Network Penetration Test', 'Red Team'];

  // Applications already being tested are left out. Being overdue and having
  // work booked are not contradictory — the work simply has not finished — and
  // opening a second engagement for the same application would put a duplicate
  // in the tester's queue.
  const openable = (rows) => rows.filter(r => !r.open_engagements);

  const createEngagements = async () => {
    setCreating(true);
    const done = [];
    const failed = [];
    // One at a time, so a failure names the application it belongs to rather
    // than collapsing into "some of them did not work".
    for (const app of queue) {
      try {
        const res = await apiFetch('/engagements', {
          method: 'POST',
          body: JSON.stringify({
            clientId,
            applicationId: app.id,
            name: app.name,
            engagementType: createType,
            customerTrackingId: app.customer_tracking_id || null,
            // Carried across so the new engagement starts with what the
            // inventory already knows, the same as picking the application on
            // the New Engagement form.
            pciRequired: !!app.pci_required,
            internetFacing: !!app.internet_facing,
            paymentFunction: !!app.payment_function,
          }),
        });
        if (res.ok) done.push(app.name);
        else {
          const d = await res.json().catch(() => ({}));
          failed.push(`${app.name}: ${d.error || `HTTP ${res.status}`}`);
        }
      } catch (err) {
        failed.push(`${app.name}: ${err.message}`);
      }
    }
    setCreating(false);
    setQueue([]);
    setCreateResult({ done, failed });
    load();
  };

  const exportCsv = (rows, filename) => {
    const esc = v => {
      const s = v == null ? '' : String(v);
      return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const header = ['Application', 'Tracking ID', 'Status', 'Last Tested', 'Next Due',
                    'Days Until Due', 'Completed Tests', 'PCI', 'Internet Facing', 'Out of Scope'];
    const body = rows.map(r => [
      r.name, r.customer_tracking_id, STATUS[r.status]?.label || r.status,
      r.last_tested || 'Never', r.next_due || '', r.days_until_due ?? '',
      r.completed_engagements, r.pci_required ? 'Yes' : 'No',
      r.internet_facing ? 'Yes' : 'No', r.out_of_scope ? 'Yes' : 'No',
    ].map(esc).join(','));
    const csv = '﻿' + [header.map(esc).join(','), ...body].join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportBreaches = (rows) => {
    const esc = v => {
      const t = v == null ? '' : String(v);
      return /[",\n\r]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t;
    };
    const header = ['Finding ID', 'Title', 'Severity', 'Status', 'Engagement', 'Application',
                    'Raised', 'Age (days)', 'SLA (days)', 'Days Over SLA'];
    const body = rows.map(r => [
      r.finding_uid, r.title, r.severity, r.status, r.engagement_name, r.application_name || '',
      r.created_at ? String(r.created_at).slice(0, 10) : '', r.age_days, r.sla_days, r.days_over_sla,
    ].map(esc).join(','));
    const csv = '﻿' + [header.map(esc).join(','), ...body].join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `sla-breaches-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const breachColumns = [
    { key: 'severity', label: 'Severity', render: v => (
      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
                     background: SEVERITY_COLOUR[v] || '#6b7280', color: '#fff' }}>{v}</span>
    )},
    { key: 'title', label: 'Finding', render: (v, row) => (
      <span>
        <span style={{ fontWeight: 600 }}>{v}</span>
        {row.finding_uid && (
          <span style={{ color: 'var(--text-secondary)', fontSize: 12, marginLeft: 6 }}>{row.finding_uid}</span>
        )}
      </span>
    )},
    { key: 'engagement_name', label: 'Engagement', render: (v, row) => (
      <span>{v}{row.application_name && (
        <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}> · {row.application_name}</span>
      )}</span>
    )},
    { key: 'status', label: 'Status' },
    { key: 'age_days', label: 'Open for', render: v => `${v}d` },
    { key: 'days_over_sla', label: 'Past SLA', render: (v, row) => (
      <span style={{ color: '#dc2626', fontWeight: 600 }}>{v}d over {row.sla_days}d</span>
    )},
  ];

  const columns = (showDue) => [
    { key: 'name', label: 'Application', render: (v, row) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 600 }}>{v}</span>
        {row.pci_required && <Badge>PCI</Badge>}
        {row.out_of_scope && (
          <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', background: '#6b7280', color: '#fff', borderRadius: 8 }}>
            OUT OF SCOPE
          </span>
        )}
      </span>
    )},
    { key: 'customer_tracking_id', label: 'Tracking ID', render: v => v || '—' },
    { key: 'status', label: 'Status', render: v => (
      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
                     background: STATUS[v]?.colour || '#6b7280', color: '#fff' }}>
        {STATUS[v]?.label || v}
      </span>
    )},
    { key: 'last_tested', label: 'Last Tested', render: v => v ? new Date(v).toLocaleDateString() : 'Never' },
    { key: 'next_due', label: showDue ? 'Due' : 'Was Due', render: (v, row) => v ? (
      <span>
        {new Date(v).toLocaleDateString()}
        {row.days_until_due != null && (
          <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
            {row.days_until_due < 0
              ? ` (${Math.abs(row.days_until_due)}d overdue)`
              : ` (in ${row.days_until_due}d)`}
          </span>
        )}
      </span>
    ) : '—' },
    { key: 'completed_engagements', label: 'Tests', render: v => v || 0 },
  ];

  const withCreateAction = (cols) => canCreateEngagements ? [...cols, {
    key: 'id', label: '', render: (v, row) => (
      row.open_engagements ? (
        <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
          Testing booked
        </span>
      ) : (
        <Button variant="secondary" style={{ padding: '4px 10px', fontSize: 12, whiteSpace: 'nowrap' }}
          onClick={e => { e.stopPropagation(); setCreateResult(null); setQueue([row]); }}>
          New engagement
        </Button>
      )
    ),
  }] : cols;

  const s = data?.summary;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Governance</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
          Whether each application is being tested as often as your policy requires.
        </p>
      </div>

      {isMspSide && (
        <Card style={{ marginBottom: 20 }}>
          <Select label="Client" style={{ marginBottom: 0, maxWidth: 400 }}
            value={clientId} onChange={e => setClientId(e.target.value)}
            options={[{ value: '', label: 'Choose a client...' },
                      ...clients.map(c => ({ value: c.id, label: c.name }))]} />
        </Card>
      )}

      {!clientId && (
        <Card style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
          <p style={{ fontSize: 16 }}>Select a client to review their testing compliance</p>
        </Card>
      )}

      {error && (
        <Card style={{ marginBottom: 20, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' }}>
          {error}
        </Card>
      )}

      {clientId && (
        <>
          {/* Testing policy */}
          <Card style={{ marginBottom: 20 }}>
            <button onClick={() => setPolicyOpen(o => !o)}
              style={{ display: 'flex', alignItems: 'baseline', gap: 10, width: '100%', textAlign: 'left',
                       background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text)' }}>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{policyOpen ? '▼' : '▶'}</span>
              <h2 style={{ fontSize: 16, fontWeight: 700 }}>Testing policy</h2>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {data?.policy?.configured
                  ? `Retest every ${describe(data.policy.testing_interval_months)} · ` +
                    `fix within ${sla.Critical}/${sla.High}/${sla.Medium}/${sla.Low} days`
                  : 'Not set — using the defaults'}
              </span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 'auto' }}>
                {policyOpen ? 'Hide' : (canEditPolicy ? 'Edit' : 'Show')}
              </span>
            </button>

            {policyOpen && (<div style={{ marginTop: 14 }}>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
              {data?.policy?.configured
                ? `Every application must be retested every ${describe(data.policy.testing_interval_months)}.`
                : 'No policy set yet — the figures below use the default of every 12 months until you save one.'}
            </p>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>
                  Retest every
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Select style={{ marginBottom: 0, minWidth: 70 }} value={String(intervalYM.years)}
                    disabled={!canEditPolicy}
                    onChange={e => setIntervalYM(i => ({ ...i, years: parseInt(e.target.value, 10) }))}
                    options={YEAR_OPTS.map(n => ({ value: String(n), label: String(n) }))} />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Years</span>
                  <Select style={{ marginBottom: 0, minWidth: 70 }} value={String(intervalYM.months)}
                    disabled={!canEditPolicy}
                    onChange={e => setIntervalYM(i => ({ ...i, months: parseInt(e.target.value, 10) }))}
                    options={MONTH_OPTS.map(n => ({ value: String(n), label: String(n) }))} />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Months</span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>
                  Warn me this far ahead
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Select style={{ marginBottom: 0, minWidth: 70 }} value={String(lookaheadYM.years)}
                    disabled={!canEditPolicy}
                    onChange={e => setLookaheadYM(l => ({ ...l, years: parseInt(e.target.value, 10) }))}
                    options={YEAR_OPTS.map(n => ({ value: String(n), label: String(n) }))} />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Years</span>
                  <Select style={{ marginBottom: 0, minWidth: 70 }} value={String(lookaheadYM.months)}
                    disabled={!canEditPolicy}
                    onChange={e => setLookaheadYM(l => ({ ...l, months: parseInt(e.target.value, 10) }))}
                    options={MONTH_OPTS.map(n => ({ value: String(n), label: String(n) }))} />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Months</span>
                </div>
              </div>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', margin: '18px 0 14px' }} />
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>PCI network segmentation</div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
              How often a PCI-flagged network must be covered by a completed Network
              Segmentation engagement. PCI DSS asks service providers for this every six
              months (11.4.6) and merchants at least annually (11.4.5), and after any
              change to the segmentation controls.
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
              <Select style={{ marginBottom: 0, minWidth: 70 }} value={String(segIntervalYM.years)}
                disabled={!canEditPolicy}
                onChange={e => setSegIntervalYM(i => ({ ...i, years: parseInt(e.target.value, 10) }))}
                options={YEAR_OPTS.map(n => ({ value: String(n), label: String(n) }))} />
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Years</span>
              <Select style={{ marginBottom: 0, minWidth: 70 }} value={String(segIntervalYM.months)}
                disabled={!canEditPolicy}
                onChange={e => setSegIntervalYM(i => ({ ...i, months: parseInt(e.target.value, 10) }))}
                options={MONTH_OPTS.map(n => ({ value: String(n), label: String(n) }))} />
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Months</span>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', margin: '18px 0 14px' }} />
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>Remediation deadlines</div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
              How many days a finding of each severity may stay open before it breaches policy.
              Informational findings have no deadline.
            </p>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              {SLA_SEVERITIES.map(sev => (
                <div key={sev}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: SEVERITY_COLOUR[sev] }}>
                    {sev}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="number" min="1" max="3650" value={sla[sev]} disabled={!canEditPolicy}
                      onChange={e => setSla(v => ({ ...v, [sev]: parseInt(e.target.value, 10) || '' }))}
                      style={{ width: 78, padding: '8px 10px', borderRadius: 6,
                               border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }} />
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>days</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 18 }}>
              {canEditPolicy && (
                <Button onClick={savePolicy} disabled={savingPolicy}>
                  {savingPolicy ? 'Saving...' : 'Save policy'}
                </Button>
              )}
              {policySaved && (
                <span style={{ color: '#16a34a', fontSize: 13 }}>Policy saved</span>
              )}
            </div>
            </div>)}
          </Card>

          {/* Which half of the question is on screen */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
            {[{ id: 'cadence', label: 'Testing cadence' },
              { id: 'segmentation', label: 'PCI segmentation' },
              { id: 'remediation', label: 'Remediation SLA' }].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{ padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                         background: 'none', border: 'none', color: tab === t.id ? 'var(--primary)' : 'var(--text-secondary)',
                         borderBottom: tab === t.id ? '2px solid var(--primary)' : '2px solid transparent' }}>
                {t.label}
                {t.id === 'segmentation' && segData?.summary?.out_of_compliance > 0 && (
                  <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: '1px 7px',
                                 borderRadius: 9, background: '#dc2626', color: '#fff' }}>
                    {segData.summary.out_of_compliance}
                  </span>
                )}
                {t.id === 'remediation' && remediation?.summary?.breached > 0 && (
                  <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: '1px 7px',
                                 borderRadius: 9, background: '#dc2626', color: '#fff' }}>
                    {remediation.summary.breached}
                  </span>
                )}
              </button>
            ))}
          </div>

          {tab === 'cadence' && (<>
          {/* Headline numbers */}
          {s && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
              <StatTile label="Compliant" value={`${s.compliance_percent == null ? '—' : s.compliance_percent + '%'}`}
                colour="#22c55e" hint={`${s.compliant} of ${s.assessed} assessed`} />
              <StatTile label="Overdue" value={s.overdue} colour={s.overdue ? '#ea580c' : undefined} />
              <StatTile label="Never tested" value={s.never_tested} colour={s.never_tested ? '#dc2626' : undefined} />
              {s.undated > 0 && (
                <StatTile label="Date missing" value={s.undated} colour="#a855f7"
                  hint="completed, but no end date" />
              )}
              <StatTile label="Due soon" value={s.due_soon} colour={s.due_soon ? '#eab308' : undefined}
                hint={`within ${describe(data.policy.lookahead_months)}`} />
              {s.internet_facing_assessed > 0 && (
                <StatTile label="Internet facing compliant"
                  value={s.internet_facing_compliance_percent == null ? '—' : s.internet_facing_compliance_percent + '%'}
                  colour={s.internet_facing_out_of_compliance ? '#dc2626' : '#22c55e'}
                  hint={`${s.internet_facing_assessed - s.internet_facing_out_of_compliance} of ${s.internet_facing_assessed} exposed applications`} />
              )}
              {s.pci_assessed > 0 && (
                <StatTile label="PCI compliant"
                  value={s.pci_compliance_percent == null ? '—' : s.pci_compliance_percent + '%'}
                  colour={s.pci_out_of_compliance ? '#dc2626' : '#22c55e'}
                  hint={`${s.pci_assessed - s.pci_out_of_compliance} of ${s.pci_assessed} PCI applications`} />
              )}
            </div>
          )}

          <Card style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={includeOutOfScope}
                onChange={e => setIncludeOutOfScope(e.target.checked)} />
              Include applications marked out of scope
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={pciOnly} onChange={e => setPciOnly(e.target.checked)} />
              PCI applications only
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={internetFacingOnly}
                onChange={e => setInternetFacingOnly(e.target.checked)} />
              Internet facing only
            </label>
            {s?.excluded_out_of_scope > 0 && !includeOutOfScope && (
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {s.excluded_out_of_scope} out-of-scope application{s.excluded_out_of_scope === 1 ? '' : 's'} excluded
              </span>
            )}
          </Card>

          {createResult && (
            <Card style={{ marginBottom: 20,
                           borderLeft: `3px solid ${createResult.failed.length ? '#ea580c' : '#22c55e'}` }}>
              {createResult.done.length > 0 && (
                <div style={{ fontSize: 14 }}>
                  <strong>
                    Opened {createResult.done.length} engagement{createResult.done.length === 1 ? '' : 's'}
                  </strong>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                    {createResult.done.join(', ')}. They start in New — set dates and assign a tester
                    on the Engagements page.
                  </div>
                </div>
              )}
              {createResult.failed.length > 0 && (
                <div style={{ fontSize: 13, color: '#dc2626', marginTop: createResult.done.length ? 10 : 0 }}>
                  <strong>Could not open {createResult.failed.length}:</strong>
                  {createResult.failed.map((f, i) => <div key={i}>{f}</div>)}
                </div>
              )}
            </Card>
          )}

          {/* Anything the numbers cannot see */}
          {s?.unlinked_complete_engagements > 0 && (
            <Card style={{ marginBottom: 20, borderLeft: '3px solid #eab308' }}>
              <strong style={{ fontSize: 14 }}>
                {s.unlinked_complete_engagements} completed engagement{s.unlinked_complete_engagements === 1 ? ' is' : 's are'} not
                linked to an application
              </strong>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                Testing only counts towards compliance when its engagement is linked to an application, so an
                application tested under one of these still reads as untested. Open the engagement and set its
                application to correct the figures.
              </div>
            </Card>
          )}

          {loading ? (
            <Card style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>Loading...</Card>
          ) : data && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                <h2 style={{ fontSize: 18, fontWeight: 700 }}>Out of compliance</h2>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  Not tested within {describe(data.policy.testing_interval_months)}
                </span>
                {data.out_of_compliance.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                    <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
                      onClick={() => exportCsv(data.out_of_compliance, 'out-of-compliance')}>Export CSV</Button>
                    {canCreateEngagements && openable(data.out_of_compliance).length > 0 && (
                      <Button style={{ padding: '4px 12px', fontSize: 12 }}
                        onClick={() => { setCreateResult(null); setQueue(openable(data.out_of_compliance)); }}>
                        New engagement for all ({openable(data.out_of_compliance).length})
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {data.out_of_compliance.length === 0 ? (
                <Card style={{ marginBottom: 28, color: 'var(--text-secondary)' }}>
                  Every assessed application has been tested within the policy window.
                </Card>
              ) : (
                <div style={{ marginBottom: 28 }}>
                  <Table columns={withCreateAction(columns(false))} data={data.out_of_compliance} />
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                <h2 style={{ fontSize: 18, fontWeight: 700 }}>Upcoming compliance needs</h2>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  Falling due within {describe(data.policy.lookahead_months)}
                </span>
                {data.due_soon.length > 0 && (
                  <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12, marginLeft: 'auto' }}
                    onClick={() => exportCsv(data.due_soon, 'upcoming-compliance')}>Export CSV</Button>
                )}
              </div>
              {data.due_soon.length === 0 ? (
                <Card style={{ marginBottom: 28, color: 'var(--text-secondary)' }}>
                  Nothing falls due in the next {describe(data.policy.lookahead_months)}.
                </Card>
              ) : (
                <div style={{ marginBottom: 28 }}>
                  <Table columns={columns(true)} data={data.due_soon} />
                </div>
              )}
            </>
          )}
          </>)}

          {tab === 'segmentation' && (loading ? (
            <Card style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>Loading...</Card>
          ) : !segData ? (
            <Card style={{ padding: 20, color: 'var(--text-secondary)' }}>
              Could not load segmentation compliance.
            </Card>
          ) : (() => {
            const S = segData.summary;
            const rows = segData.networks;
            const outOfCompliance = rows.filter(r => r.status === 'overdue' || r.status === 'never_tested');
            const dueSoon = rows.filter(r => r.status === 'due_soon');

            const segColumns = (showDue) => [
              { key: 'value', label: 'Network', render: (v, row) => (
                <div>
                  <div style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 13 }}>{v}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {row.name}{row.location ? ` · ${row.location}` : ''}
                  </div>
                </div>
              )},
              { key: 'pci', label: 'PCI', render: v => v
                ? <Badge style={{ background: 'rgba(239,68,68,0.12)', color: '#dc2626' }}>PCI</Badge>
                : <span style={{ color: 'var(--text-muted)' }}>—</span> },
              { key: 'tags', label: 'Tags', render: v => (v && v.length)
                ? <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{v.join(', ')}</span>
                : <span style={{ color: 'var(--text-muted)' }}>—</span> },
              // Which test it was. "Overdue" on its own leaves the reader
              // hunting for what the last one covered; naming it means the
              // next question — run that again — is one click away.
              { key: 'last_engagement_name', label: 'Last tested by', render: (v, row) => row.last_tested ? (
                <a href={`/engagements/${row.last_engagement_id}`}
                   onClick={e => { e.preventDefault(); navigate(`/engagements/${row.last_engagement_id}`); }}
                   style={{ color: 'var(--primary)', fontSize: 13, textDecoration: 'none' }}>
                  {v || row.last_engagement_type || 'Engagement'}
                </a>
              ) : <span style={{ color: 'var(--text-muted)' }}>Never</span> },
              // The date in its own column. It is the engagement's end date —
              // when the testing finished, which is what the policy window is
              // measured from, not when the engagement was raised or reported.
              { key: 'last_tested', label: 'Last tested', render: (v, row) => v ? (
                <div>
                  <div style={{ fontSize: 13 }}>{new Date(v).toLocaleDateString()}</div>
                  {row.days_until_due != null && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {(() => {
                        const days = Math.round((Date.now() - new Date(v)) / 86400000);
                        const months = Math.round(days / 30.44);
                        return months >= 2 ? `${months} months ago` : `${days} days ago`;
                      })()}
                    </div>
                  )}
                </div>
              ) : <span style={{ color: 'var(--text-muted)' }}>&mdash;</span> },
              ...(showDue ? [{ key: 'next_due', label: 'Due', render: (v, row) => (
                <span>{v ? new Date(v).toLocaleDateString() : '—'}
                  {row.days_until_due != null && (
                    <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                      {' '}({row.days_until_due}d)
                    </span>
                  )}
                </span>
              )}] : [{ key: 'status', label: 'Status', render: (v, row) => v === 'never_tested'
                ? <Badge style={{ background: 'rgba(100,116,139,0.15)', color: 'var(--text-secondary)' }}>Never tested</Badge>
                : <Badge style={{ background: 'rgba(239,68,68,0.12)', color: '#dc2626' }}>
                    {Math.abs(row.days_until_due)}d overdue
                  </Badge> }]),
              { key: 'open_engagements', label: '', render: (v, row) => (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {/* Already booked. Offering to clone without saying so is
                      how an engagement gets created twice. */}
                  {v > 0 && (
                    <span style={{ fontSize: 11, color: '#f59e0b' }}>
                      {v} test{v === 1 ? '' : 's'} already booked
                    </span>
                  )}
                  {canCreateEngagements && row.last_engagement_id && (
                    <Button variant="secondary" style={{ padding: '4px 10px', fontSize: 12 }}
                      disabled={cloning === row.id}
                      title="Create a new engagement of the same type with the same scope"
                      onClick={() => cloneTest(row)}>
                      {cloning === row.id ? 'Cloning…' : 'Clone test'}
                    </Button>
                  )}
                </div>
              )},
            ];

            const stat = (label, value, color) => (
              <Card style={{ padding: 14, flex: 1, minWidth: 130 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                <div style={{ fontSize: 24, fontWeight: 700, color, marginTop: 4 }}>{value}</div>
              </Card>
            );

            return (<>
              {/* Nothing measured is not the same as nothing wrong.
                  With no networks on record every tile reads zero, and a row
                  of zeros -- "Overdue 0" in red, "Compliant 0" in green -- is
                  read as a pass, especially once it is screenshotted into a
                  compliance conversation. A false clean is the one answer this
                  page must never give, so when there is nothing to assess it
                  says so instead of scoring it. */}
              {S.assessed === 0 ? (
                <Card style={{ padding: 16, marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 20, lineHeight: 1 }}>&#9675;</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>Not yet measured</div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      {segAllNetworks
                        ? 'No networks on record for this client, so segmentation compliance cannot be assessed. '
                        : 'No PCI-flagged networks on record for this client, so segmentation compliance cannot be assessed. '}
                      This is not a pass: it means nothing is being tracked yet.
                      {canCreateEngagements && (
                        <>
                          {' '}
                          <a href="/networks"
                             onClick={e => { e.preventDefault(); navigate('/networks'); }}
                             style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                            Add networks
                          </a>
                          {' and flag the ones in PCI scope.'}
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              ) : (
                <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                  {stat('Assessed', S.assessed, 'var(--primary)')}
                  {stat('Compliant', S.compliant, '#16a34a')}
                  {stat('Due soon', S.due_soon, '#f59e0b')}
                  {stat('Overdue', S.overdue, '#dc2626')}
                  {stat('Never tested', S.never_tested, 'var(--text-secondary)')}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  Segmentation proved every {describe(segData.policy.segmentation_interval_months)}
                </span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', color: 'var(--text-secondary)' }}>
                  <input type="checkbox" checked={segAllNetworks}
                    onChange={e => setSegAllNetworks(e.target.checked)} />
                  Include networks not flagged PCI
                </label>
              </div>

              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>
                Out of compliance ({outOfCompliance.length})
              </h3>
              {outOfCompliance.length === 0 ? (
                <Card style={{ marginBottom: 28, color: 'var(--text-secondary)' }}>
                  {rows.length === 0
                    ? 'No PCI-flagged networks on record for this client. Flag them on the Networks page and they will be measured here.'
                    : 'Every PCI network has been covered by a segmentation test within the policy window.'}
                </Card>
              ) : (
                <div style={{ marginBottom: 28 }}>
                  <Table columns={segColumns(false)} data={outOfCompliance} />
                </div>
              )}

              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>
                Upcoming ({dueSoon.length})
              </h3>
              {dueSoon.length === 0 ? (
                <Card style={{ color: 'var(--text-secondary)' }}>
                  Nothing due within {describe(segData.policy.lookahead_months)}.
                </Card>
              ) : (
                <Table columns={segColumns(true)} data={dueSoon} />
              )}
            </>);
          })())}

          {tab === 'remediation' && (loading ? (
            <Card style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>Loading...</Card>
          ) : !remediation ? (
            <Card style={{ color: 'var(--text-secondary)' }}>Could not load remediation data.</Card>
          ) : (<>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
              <StatTile label="Within SLA"
                value={remediation.summary.on_time_percent == null ? '—' : remediation.summary.on_time_percent + '%'}
                colour={remediation.summary.breached ? '#ea580c' : '#22c55e'}
                hint={`${remediation.summary.within_sla} of ${remediation.summary.open} open findings`} />
              <StatTile label="Past deadline" value={remediation.summary.breached}
                colour={remediation.summary.breached ? '#dc2626' : undefined} />
              <StatTile label="Open findings" value={remediation.summary.open}
                hint="Open, Reopened or Retest" />
            </div>

            <Card style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={pciOnly} onChange={e => setPciOnly(e.target.checked)} />
                PCI applications only
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={internetFacingOnly}
                  onChange={e => setInternetFacingOnly(e.target.checked)} />
                Internet facing only
              </label>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                Findings on out-of-scope applications are included: a finding already raised still needs fixing.
              </span>
            </Card>

            {/* Where the time is going, by severity */}
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>By severity</h2>
            <div style={{ marginBottom: 28 }}>
              <Table
                columns={[
                  { key: 'severity', label: 'Severity', render: v => (
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
                                   background: SEVERITY_COLOUR[v] || '#6b7280', color: '#fff' }}>{v}</span>
                  )},
                  { key: 'sla_days', label: 'Deadline', render: v => `${v} days` },
                  { key: 'open', label: 'Open' },
                  { key: 'breached', label: 'Past deadline', render: v => (
                    <span style={{ color: v ? '#dc2626' : 'var(--text-secondary)', fontWeight: v ? 700 : 400 }}>{v}</span>
                  )},
                  { key: 'oldest_open_days', label: 'Oldest open', render: v => v == null ? '—' : `${v}d` },
                  { key: 'mean_days_to_close', label: 'Avg. time to fix', render: (v, row) => v == null
                    ? <span style={{ color: 'var(--text-secondary)' }}>no data</span>
                    : <span>{v}d <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                        (over {row.closed_measured})</span></span> },
                  { key: 'closed_within_sla_percent', label: 'Fixed in time', render: v => v == null ? '—' : `${v}%` },
                ]}
                data={remediation.by_severity} />
            </div>

            {/* How long outstanding work has been outstanding */}
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Ageing</h2>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 28 }}>
              {remediation.aging.map(b => (
                <StatTile key={b.label} label={b.label} value={b.count}
                  colour={b.label === 'Over 90 days' && b.count ? '#dc2626' : undefined}
                  hint={b.critical_high ? `${b.critical_high} Critical or High` : undefined} />
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
              <h2 style={{ fontSize: 18, fontWeight: 700 }}>Past deadline</h2>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Open longer than the policy allows</span>
              {remediation.breaches.length > 0 && (
                <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12, marginLeft: 'auto' }}
                  onClick={() => exportBreaches(remediation.breaches)}>Export CSV</Button>
              )}
            </div>
            {remediation.breaches.length === 0 ? (
              <Card style={{ marginBottom: 28, color: 'var(--text-secondary)' }}>
                Every open finding is inside its remediation deadline.
              </Card>
            ) : (
              <div style={{ marginBottom: 28 }}>
                <Table columns={breachColumns} data={remediation.breaches}
                  onRowClick={row => navigate(`/engagements/${row.engagement_id}`)} />
              </div>
            )}

            {remediation.summary.closures_missing_date > 0 && (
              <Card style={{ marginBottom: 20, borderLeft: '3px solid #eab308' }}>
                <strong style={{ fontSize: 14 }}>
                  {remediation.summary.closures_missing_date} resolved finding
                  {remediation.summary.closures_missing_date === 1 ? ' has' : 's have'} no resolution date
                </strong>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                  These were closed before the date started being recorded, so they are left out of the averages
                  above rather than being guessed at. Findings closed from now on are timed properly.
                </div>
              </Card>
            )}
          </>))}
        </>
      )}
      <Modal isOpen={queue.length > 0} onClose={() => setQueue([])}
        title={queue.length === 1 ? 'Open an engagement' : `Open ${queue.length} engagements`} width={520}>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
          {queue.length === 1
            ? 'This opens an engagement for the application below, linked to it so the testing counts towards its compliance.'
            : 'This opens one engagement per application below, each linked to its application so the testing counts towards compliance.'}
          {' '}They start in New with no dates — scoping and scheduling happen on the Engagements page.
        </p>
        <Select label="What kind of test?" value={createType}
          onChange={e => setCreateType(e.target.value)}
          options={ENGAGEMENT_TYPES.map(t => ({ value: t, label: t }))} />
        <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)',
                      borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
          {queue.map(a => (
            <div key={a.id} style={{ fontSize: 13, padding: '3px 0' }}>
              {a.name}
              {a.customer_tracking_id && (
                <span style={{ color: 'var(--text-secondary)' }}> · {a.customer_tracking_id}</span>
              )}
            </div>
          ))}
        </div>
        {data && openable(data.out_of_compliance).length < data.out_of_compliance.length && queue.length > 1 && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
            {data.out_of_compliance.length - openable(data.out_of_compliance).length} application
            {data.out_of_compliance.length - openable(data.out_of_compliance).length === 1 ? ' is' : 's are'} left
            out because testing is already booked for them.
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setQueue([])}>Cancel</Button>
          <Button onClick={createEngagements} disabled={creating}>
            {creating ? 'Opening...' : queue.length === 1 ? 'Open engagement' : `Open ${queue.length} engagements`}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
